/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactElement,  useEffect,  useState } from 'react';
import { Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, CircularProgress } from '@mui/material';
import { CanvasEnvironment } from '../../../utils/canvas/canvas-environment';
import Paper, { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import { uuid } from '../../../utils/utils';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { toast } from 'react-toastify';
import * as CanvasActions from '../../../utils/canvas/canvas-actions';

function PaperComponent(props: PaperProps): ReactElement {
    const nodeRef = React.useRef(null);
    return (
        <Draggable nodeRef={nodeRef} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}


const OdnaComment = (props: { open: boolean, env: CanvasEnvironment | null, onClose: () => void } ): React.ReactElement => {
    const {open, env, onClose} = props;
    const [comment, setComment] = useState('');
    const [isRunning, setIsRunning] = React.useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const uuidRef = React.useRef(uuid());


    useEffect(() => {
        if (open && env) {
            let selection = env.selection[0];
            if (selection && env.currentLinkedOccurrenceDna) {
                const dnaLink = selection.get('nodeClass') === 'EVENTNODE' ?
                    env.currentLinkedOccurrenceDna?.events.find((event) => event.id === selection.get('data').id) :
                    env.currentLinkedOccurrenceDna?.faultTreeNodes.find((node) => node.id === selection.get('data').id);
                setComment(dnaLink ? dnaLink.justification : '');
            } else {
                setComment('');
            }
        }
    }, [env, open]);

    useEffect(() => {
        if (comment.length < 10 || comment.length > 10000) {
            setError('Justification must be between 10 and 10,000 characters.');
        } else {
            setError(null);
        }
    }, [comment]);

    const handleSave = async (): Promise<void> => {
        let selection = env?.selection[0];
        if (selection && env?.currentLinkedOccurrenceDna) {
            let dnaLink = selection.get('nodeClass') === 'EVENTNODE' ?
                env?.currentLinkedOccurrenceDna?.events.find((event) => event.id === selection.get('data').id) :
                env?.currentLinkedOccurrenceDna?.faultTreeNodes.find((node) => node.id === selection.get('data').id);
            if (dnaLink) {
                dnaLink.justification = comment;
                env?.saveLinkedOccurrenceDna();
            }
        }
    };

    return (
        <Dialog open={open}
            onClose={onClose}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            fullWidth
            maxWidth='sm'
        >
            <DialogTitle style={{ cursor: 'move'}} id='draggable-dialog-title'>Add Justification</DialogTitle>
            <DialogContent dividers>
                <TextField
                    required
                    autoFocus
                    margin="dense"
                    id={`comment-id-${uuidRef.current}`}
                    label="Justification"
                    type="text"
                    multiline
                    rows={4}
                    value={comment}
                    onChange={(e): void => {
                        setComment(e.target.value);
                    }}
                    error={!!error}
                    helperText={error || `${comment.length}/10000 characters`}
                    sx={{
                        '& input': {
                            border: 'none',
                        }
                    }}
                    slotProps={{
                        input: {
                            'role': 'textbox',
                            'tabIndex': 0,
                            'aria-labelledby': `comment-id-${uuidRef.current}`,
                            'aria-label': 'comment',
                        }
                    }}
                    fullWidth

                />
            </DialogContent>
            <DialogActions>
                <Button
                    variant='contained'
                    color='secondary'
                    startIcon={<CancelIcon />}
                    onClick={onClose}>
                        Cancel
                </Button>
                <Button
                    variant='contained'
                    color="primary"
                    startIcon={isRunning ? <CircularProgress size={20} /> : <SaveAltIcon />}
                    onClick={async (): Promise<void> => {
                        setIsRunning(true);
                        try {
                            await handleSave();
                            toast.success('Comment saved successfully');
                        } catch (e) {
                            toast.error('Failed to save comment');
                        } finally {
                            setIsRunning(false);
                            onClose();
                        }
                    }}
                    disabled={!!error || isRunning || !CanvasActions.canAddOrEditResource(env)}
                >
                        Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OdnaComment;